import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'reactstrap';
import ActionsPanel from "../../commons/components/tableComponents/ActionsPanel";
import ActiveTextIndicator from "../../commons/components/tableComponents/ActiveTextIndicator";
import ToggleSwitch from "../../commons/components/tableComponents/ToggleSwitch";
import Table from '../../commons/components/Table';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { getCourseType, getCategories, getSubcategories } from "../../redux/actions/categoriesActions";
import { getMentors } from "../../redux/actions/usersActions";
import { getCourses, updateCourse, deleteCourse } from "../../redux/actions/coursesActions";
import { rowsPerPageOptions } from "../../commons/constants/pagination";
import { Link } from 'react-router-dom';
import CourseFilterBar from './CourseFilterBar';

const CourseIdLink = ({ initialValue }) => (
    <Link
        to={`/cursos/editarCurso/${initialValue}`}
        style={{ color: '#131F2A', fontWeight: 600, textDecoration: 'underline', textDecorationColor: 'rgba(19,31,42,0.35)', textUnderlineOffset: '3px' }}
    >
        {initialValue}
    </Link>
);

const PANEL_FILTER_KEYS = [
    'course_type_id',
    'is_active',
    'is_free',
    'is_featured',
    'is_free_public_user',
    'assessment_type',
    'subcategory__category_id',
    'subcategory_id',
    'mentor_id',
];

const EMPTY_FILTERS = {
    title__icontains: '',
    code: '',
    course_type_id: '',
    is_active: '',
    is_free: '',
    is_featured: '',
    is_free_public_user: '',
    assessment_type: '',
    subcategory__category_id: '',
    subcategory_id: '',
    mentor_id: '',
};

const columnsTable = [
    {
        value: 'id',
        title: 'ID',
        isCustom: true,
        notIsSortable: true,
        CustomElement: CourseIdLink,
    },
    {
        value: 'code',
        title: 'Cod',
        isCustom: false
    },
    {
        value: 'title',
        title: 'Nombre del curso',
        isCustom: false
    },
    {
        value: 'subcategory',
        title: 'Subcategoría',
        isCustom: false
    },
    {
        value: 'courseType',
        title: 'Tipo de curso',
        isCustom: false
    },
    {
        value: 'subcategory',
        title: 'Categoría',
        isCustom: false
    },
    {
        value: 'is_free',
        title: 'Gratuito',
        isCustom: true,
        CustomElement: ToggleSwitch,
        state: 'courses.courses',
    },
    {
        value: 'is_free_public_user',
        title: 'Gratuito P.',
        isCustom: true,
        CustomElement: ToggleSwitch,
        state: 'courses.courses',
    },
    {
        value: 'is_featured',
        title: 'Destacado',
        isCustom: true,
        CustomElement: ToggleSwitch,
        state: 'courses.courses',
    },
    {
        value: 'is_active',
        title: 'Estado',
        isCustom: true,
        CustomElement: ActiveTextIndicator
    },
    {
        value: 'is_active',
        title: 'Acciones',
        isCustom: true,
        notIsSortable: true,
        CustomElement: ActionsPanel,
        updateDispatchAction: updateCourse,
        editionRedirect: true,
        editionRedirectURL: '/cursos/editarCurso',
        state: 'courses.courses',
        deleteOption: true,
        deleteDispatchAction: deleteCourse
    }
]

const handleData = (data) => (
    data.map(({ id, code, title, subcategory, course_type, is_free, is_free_public_user, is_featured, is_active }) => ({
        id,
        code,
        title,
        subcategory: subcategory.name,
        courseType: course_type.name,
        category: subcategory?.category?.name,
        is_free,
        is_free_public_user,
        is_featured,
        is_active,
        _actions: null, // posición 10 — requerida para que el Table renderice la celda de Acciones
    }))
)

const filtersFromSearchParams = (searchParams) => {
    const state = { ...EMPTY_FILTERS };
    Object.keys(EMPTY_FILTERS).forEach(key => {
        const val = searchParams.get(key);
        if (val !== null) state[key] = val;
    });
    return state;
};

const buildActiveFilters = (filters) =>
    Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''));

const Courses = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const pageParam = searchParams.get('page');
    const rowsPerPageParam = searchParams.get('rowsPerPage');

    const dispatch = useDispatch();
    const coursesList = useSelector((state) => state.courses.courses);
    const courseTypeList = useSelector((state) => state.categories.courseType?.data ?? []);
    const categoriesList = useSelector((state) => state.categories.categories?.data ?? []);
    const subcategoriesList = useSelector((state) => state.categories.subcategories?.data ?? []);
    const mentorsList = useSelector((state) => state.users.mentors?.data ?? []);
    const isLoading = useSelector((state) => state.courses.isFetching);
    const loadingDropdowns = useSelector((state) => state.categories.isFetching || state.users.isFetching);

    const refTable = useRef(null);
    const navigate = useNavigate();

    const [filterState, setFilterState] = useState(() => filtersFromSearchParams(searchParams));

    const activeFilters = useMemo(() => buildActiveFilters(filterState), [filterState]);

    const activePanelFilterCount = useMemo(
        () => PANEL_FILTER_KEYS.filter(k => filterState[k] !== '').length,
        [filterState]
    );

    useEffect(() => {
        dispatch(getCourseType({ data_all: "YES" }));
        dispatch(getCategories({ data_all: "YES" }));
        dispatch(getSubcategories({ data_all: "YES" }));
        dispatch(getMentors({ data_all: "YES" }));

        const initialFilters = filtersFromSearchParams(searchParams);
        dispatch(getCourses({
            page: pageParam ? parseInt(pageParam) + 1 : 1,
            page_size: rowsPerPageParam || rowsPerPageOptions[0],
            ...buildActiveFilters(initialFilters),
        }, false));
    }, []);

    const handleFilterChange = (key, value) => {
        setFilterState(prev => ({ ...prev, [key]: value }));
    };

    const handleSearch = () => {
        const current = buildActiveFilters(filterState);
        dispatch(getCourses({ page: 1, page_size: rowsPerPageOptions[0], ...current }, false));
        setSearchParams({ page: 0, rowsPerPage: rowsPerPageOptions[0], ...current });
        refTable.current?.resetPagination();
        refTable.current?.resetRowsPerPage();
    };

    const handleClearFilters = () => {
        const cleared = { ...filterState };
        PANEL_FILTER_KEYS.forEach(key => { cleared[key] = ''; });
        setFilterState(cleared);
        const barFilters = buildActiveFilters({
            title__icontains: cleared.title__icontains,
            code: cleared.code,
        });
        dispatch(getCourses({ page: 1, page_size: rowsPerPageOptions[0], ...barFilters }, false));
        setSearchParams({ page: 0, rowsPerPage: rowsPerPageOptions[0], ...barFilters });
        refTable.current?.resetPagination();
        refTable.current?.resetRowsPerPage();
    };

    const handleUpdateCourse = (newValues, { id, code, title, description, course_material_url, course_audio_url, estimated_duration, course_cover_url, course_price, is_free, is_free_public_user, is_active, is_featured, has_pre_test, has_post_test, course_type, subcategory, mentor }) => {
        const body = {
            id,
            code,
            title,
            description,
            course_material_url,
            course_audio_url,
            estimated_duration,
            course_cover_url,
            course_price,
            is_free,
            is_free_public_user,
            is_active,
            is_featured,
            has_pre_test,
            has_post_test,
            course_type_id: course_type.id,
            subcategory_id: subcategory.id,
            mentor_id: mentor.id
        }
        dispatch(updateCourse(
            { ...body, ...newValues },
            {
                page: refTable.current.page + 1,
                page_size: refTable.current.rowsPerPage,
                ...activeFilters,
            },
            1
        ))
    }

    return (
        <div className='coursesContainer'>
            <div className='headerContainer'>
                <div className='leftHeaderContainer'>
                    <h4>Mis Cursos</h4>
                </div>
                <button className='addItemButton' onClick={() => navigate(`/cursos/crearCurso`)}>
                    Subir un curso
                </button>
            </div>
            <div className='filterSection'>
                <CourseFilterBar
                    filters={filterState}
                    onChange={handleFilterChange}
                    onSearch={handleSearch}
                    onClear={handleClearFilters}
                    courseTypes={courseTypeList}
                    categories={categoriesList}
                    subcategories={subcategoriesList}
                    mentors={mentorsList}
                    loadingDropdowns={loadingDropdowns}
                    loadingResults={isLoading}
                    activePanelFilterCount={activePanelFilterCount}
                />
            </div>
            {
                coursesList
                    ? <div className='tableWrapper'><Table
                        columns={columnsTable}
                        data={handleData(coursesList.data)}
                        changePage={(newPage, pageSize) => dispatch(getCourses({ page: newPage + 1, page_size: pageSize, ...activeFilters }, false))}
                        changeRowsPerPage={(pageSize) => dispatch(getCourses({ page: 1, page_size: pageSize, ...activeFilters }, false))}
                        count={coursesList.count}
                        ref={refTable}
                        updateData={handleUpdateCourse}
                        dataFilter={activeFilters}
                        disableDefaultSort
                    /></div>
                    : <Spinner style={{ marginLeft: "45%", marginTop: "46px" }} />
            }
        </div>
    )
}

export default Courses;
